import React, { useContext, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from "@mui/material";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import performRequest from "network/perform-request";
import { leagueCustomerAdministratorsRoute } from "network/api-routes";
import {
  ADMIN_ROLES,
  adminErrorMessageId,
  adminRoleHintId,
  adminRoleLabelId,
  buildInvitePayload,
  emptyInviteForm,
  validateInviteForm,
} from "components/pages/Gestion/gestion-administrators-helpers";

// Server validation keys (Laravel errors) mapped to the form fields.
const FIELD_BY_SERVER_KEY = {
  first_name: "firstName",
  last_name: "lastName",
  email: "email",
  phone: "phone",
  gestion_role: "role",
};

/**
 * Invites a person as a Gestión administrator with name, contact and role.
 */
const GestionAdministratorInviteDialog = ({ open, onClose, customerId, onInvited }) => {
  const { user: authUser } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState(emptyInviteForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(emptyInviteForm());
    setErrors({});
  }, [open]);

  const updateField = (field) => (event) => {
    const { value } = event.target;
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
  };

  const showServerError = (error) => {
    const messageId = adminErrorMessageId(error);
    if (messageId) {
      if (error.response.data.code === "already_administrator") {
        setErrors({ email: intl.formatMessage({ id: messageId }) });
      } else {
        enqueueSnackbar(intl.formatMessage({ id: messageId }), { variant: "error" });
      }
      return;
    }

    const serverErrors = error?.response?.data?.errors || {};
    const fieldErrors = {};
    Object.entries(serverErrors).forEach(([key, messages]) => {
      const field = FIELD_BY_SERVER_KEY[key];
      if (field && messages?.[0]) fieldErrors[field] = messages[0];
    });

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    enqueueSnackbar(intl.formatMessage({ id: "gestion.error.generic" }), { variant: "error" });
  };

  const submit = () => {
    const validation = validateInviteForm(form);
    const clientErrors = Object.fromEntries(
      Object.entries(validation).map(([field, messageId]) => [field, intl.formatMessage({ id: messageId })]),
    );
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    const payload = buildInvitePayload(form);
    setErrors({});
    setSaving(true);

    performRequest("POST", leagueCustomerAdministratorsRoute(customerId), payload, true, authUser)
      .then((response) => {
        enqueueSnackbar(intl.formatMessage({ id: "gestion.admins.invite.sent" }, { email: payload.email }), {
          variant: "success",
        });
        onInvited?.(response.data);
        onClose();
      })
      .catch(showServerError)
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{intl.formatMessage({ id: "gestion.admins.invite.title" })}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            fullWidth
            size="small"
            name="lastName"
            label={intl.formatMessage({ id: "gestion.admins.invite.lastName" })}
            value={form.lastName}
            onChange={updateField("lastName")}
            error={Boolean(errors.lastName)}
            helperText={errors.lastName || " "}
          />
          <TextField
            fullWidth
            size="small"
            name="firstName"
            label={intl.formatMessage({ id: "gestion.admins.invite.firstName" })}
            value={form.firstName}
            onChange={updateField("firstName")}
            error={Boolean(errors.firstName)}
            helperText={errors.firstName || " "}
          />
          <TextField
            fullWidth
            size="small"
            name="email"
            type="email"
            label={intl.formatMessage({ id: "gestion.admins.invite.email" })}
            value={form.email}
            onChange={updateField("email")}
            error={Boolean(errors.email)}
            helperText={errors.email || " "}
          />
          <TextField
            fullWidth
            size="small"
            name="phone"
            label={intl.formatMessage({ id: "gestion.admins.invite.phone" })}
            value={form.phone}
            onChange={updateField("phone")}
            error={Boolean(errors.phone)}
            helperText={errors.phone || intl.formatMessage({ id: "gestion.admins.invite.phoneHint" })}
          />
          <TextField
            select
            fullWidth
            size="small"
            name="role"
            label={intl.formatMessage({ id: "gestion.admins.invite.role" })}
            value={form.role}
            onChange={updateField("role")}
            error={Boolean(errors.role)}
            helperText={errors.role || intl.formatMessage({ id: adminRoleHintId(form.role) })}
            SelectProps={{ native: true }}
          >
            {ADMIN_ROLES.map((role) => (
              <option key={role} value={role}>
                {intl.formatMessage({ id: adminRoleLabelId(role) })}
              </option>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {intl.formatMessage({ id: "Cancelar" })}
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {intl.formatMessage({ id: "gestion.admins.invite.send" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GestionAdministratorInviteDialog;
